import {
  AdproductsMoodysReportType,
  type CustomerAnalyticsMoodysMarketPerformanceDto,
  type CustomerAnalyticsMoodysReportDto,
} from '@zg-rentals/ts-customer-analytics-client';
import { MarketTrendsColumn, MarketTrendsPageWrapper, MarketTrendsTextHeader } from './MarketTrendsComponents';
import { Image, Text, View } from '@react-pdf/renderer';
import { styles } from './pdfStyles';
import { getRowStyle } from './pdfImageUtils';
import { formatCurrency } from '../formatCurrency';

export const PerformancePage = ({
  report,
  performanceChart,
}: {
  report: CustomerAnalyticsMoodysReportDto;
  performanceChart: string;
}) => {
  const hasEffectiveRent = report.performanceReport.some((performanceReport) => performanceReport.effectiveRentPerUnit);
  const totalCols = hasEffectiveRent ? 8 : 6;
  const colWidth = 100 / totalCols;
  const threeYearsAgo = new Date().getFullYear() - 3;

  const data: Array<CustomerAnalyticsMoodysMarketPerformanceDto> = report.performanceReport
    .sort((a, b) => a.quarter.localeCompare(b.quarter))
    .filter((row) => row.year >= threeYearsAgo)
    .sort((a, b) => a.year - b.year);

  return (
    <MarketTrendsPageWrapper quarter={report.dataAsOfQuarter} year={report.dataAsOfYear}>
      <View style={styles.containerBase}>
        <MarketTrendsTextHeader
          headerText={
            (report.reportType === AdproductsMoodysReportType.SUBMARKET ? 'Submarket' : 'Market') +
            ' Performance by Quarter'
          }
          paddingBottom={20}
        />
        <View style={{ flexDirection: 'row', width: '100%', justifyContent: 'center', marginBottom: 40 }}>
          <View>
            <Image src={performanceChart} />
          </View>
        </View>
        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <MarketTrendsColumn width={colWidth}>
              <Text>Year</Text>
            </MarketTrendsColumn>
            <MarketTrendsColumn width={colWidth}>
              <Text>Quarter</Text>
            </MarketTrendsColumn>
            <MarketTrendsColumn width={colWidth}>
              <Text>Asking Rent</Text>
              <Text style={styles.tableSubHeading}>Per Unit</Text>
            </MarketTrendsColumn>
            <MarketTrendsColumn width={colWidth}>
              <Text>Asking Rent</Text>
              <Text style={styles.tableSubHeading}>% Change</Text>
            </MarketTrendsColumn>
            {hasEffectiveRent && (
              <>
                <MarketTrendsColumn width={colWidth}>
                  <Text>Effective Rent</Text>
                  <Text style={styles.tableSubHeading}>Per Unit</Text>
                </MarketTrendsColumn>
                <MarketTrendsColumn width={colWidth}>
                  <Text>Effective Rent</Text>
                  <Text style={styles.tableSubHeading}>% Change</Text>
                </MarketTrendsColumn>
              </>
            )}
            <MarketTrendsColumn width={colWidth}>
              <Text>% Vacant</Text>
            </MarketTrendsColumn>
            <MarketTrendsColumn width={colWidth}>
              <Text>% Vacant</Text>
              <Text style={styles.tableSubHeading}>Change in BPS</Text>
            </MarketTrendsColumn>
          </View>
          {data.map((reportRow, i) => (
            <View style={getRowStyle(i, reportRow.quarter === 'Y')} key={reportRow.year}>
              <MarketTrendsColumn width={colWidth}>
                <Text>{reportRow.year}</Text>
              </MarketTrendsColumn>
              <MarketTrendsColumn width={colWidth}>
                <Text>{reportRow.quarter}</Text>
              </MarketTrendsColumn>
              <MarketTrendsColumn width={colWidth}>
                <Text>{formatCurrency(reportRow.askingRentPerUnit)}</Text>
              </MarketTrendsColumn>
              <MarketTrendsColumn width={colWidth}>
                <Text>{(reportRow.askingRentChange * 100).toFixed(1) + '%'}</Text>
              </MarketTrendsColumn>
              {hasEffectiveRent && (
                <>
                  <MarketTrendsColumn width={colWidth}>
                    <Text>{formatCurrency(reportRow.effectiveRentPerUnit)}</Text>
                  </MarketTrendsColumn>
                  <MarketTrendsColumn width={colWidth}>
                    <Text>{(reportRow.effectiveRentChange * 100).toFixed(1) + '%'}</Text>
                  </MarketTrendsColumn>
                </>
              )}
              <MarketTrendsColumn width={colWidth}>
                <Text>{(reportRow.vacancy * 100).toFixed(1) + '%'}</Text>
              </MarketTrendsColumn>
              <MarketTrendsColumn width={colWidth}>
                <Text>{reportRow.vacancyChangeBps.toLocaleString()}</Text>
              </MarketTrendsColumn>
            </View>
          ))}
        </View>
      </View>
    </MarketTrendsPageWrapper>
  );
};
